import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { PRODUCT_KINDS } from '@gk/contracts'
import { latestVersions, unitSymbol, type BatchRow, type ProductRow, type ProductVersionRow, type SupplierRow } from '../../data/catalog.ts'
import { densityLabel, densityOf, filterProducts, type KindFilter } from '../../data/catalogueUi.ts'
import { useClub } from '../../club/ClubProvider.tsx'
import { localDate } from '../../lib/time.ts'
import { Field, Pill } from '../../ui/components.tsx'
import { useWorkshopAction } from '../../ui/hooks.ts'
import type { Pending } from '../../sync/merge.ts'
import { ProductDetail } from './ProductDetail.tsx'
import { NewProductForm } from './ProductForms.tsx'
import { Chevron, Feedback, useCatalogueAccess, useLang } from './shared.tsx'

const NONE: never[] = []
function groupBy<T>(rows: readonly T[], key: (r: T) => string): Map<string, T[]> {
  const out = new Map<string, T[]>()
  for (const r of rows) { const k = key(r); const list = out.get(k); if (list) list.push(r); else out.set(k, [r]) }
  return out
}

export interface CatalogueData { suppliers: SupplierRow[]; products: Array<Pending<ProductRow>>; versions: ProductVersionRow[]; batches: BatchRow[] }

/** The products: search, filter by kind, archived on request; each row opens into its versions, batches, converter and (for managers) forms. `focusId` is the product a link points at. */
export function ProductsTab({ data, focusId }: { data: CatalogueData; focusId?: string }) {
  const { t } = useTranslation()
  const lang = useLang()
  const { site } = useClub()
  const access = useCatalogueAccess()
  const w = useWorkshopAction()
  const [query, setQuery] = useState('')
  const [kind, setKind] = useState<KindFilter>('all')
  const [showArchived, setShowArchived] = useState(false)
  const [open, setOpen] = useState<string | null>(focusId ?? null)
  const [creating, setCreating] = useState(false)
  useEffect(() => { if (focusId) setOpen(focusId) }, [focusId])
  const newest = useMemo(() => latestVersions(data.versions), [data.versions])
  const versionsOf = useMemo(() => groupBy(data.versions, (v) => v.product_id), [data.versions])
  const batchesOf = useMemo(() => groupBy(data.batches, (b) => b.product_id), [data.batches])
  const supplierName = useMemo(() => new Map(data.suppliers.map((s) => [s.id, s.name])), [data.suppliers])
  const rows = useMemo(() => filterProducts(data.products, { query, kind, showArchived, pinnedId: open }), [data.products, query, kind, showArchived, open])
  const today = localDate(new Date(), site.timezone)

  if (creating) {
    return <NewProductForm suppliers={data.suppliers} w={w} access={access} onClose={() => { w.reset(); setCreating(false) }} onCreated={(id) => { setCreating(false); setOpen(id); setQuery(''); setKind('all') }} />
  }
  return (
    <section className="flex flex-col gap-4" data-testid="products-panel">
      {access.allowed && <button type="button" className="btn btn-primary !min-h-11 self-start" onClick={() => { w.reset(); setCreating(true) }} data-testid="product-new">{t('catalogue.products.new')}</button>}
      <Feedback id="catalogue" error={w.error} ok={w.ok} pending={w.pending} />
      <div className="flex flex-col gap-3">
        <Field label={t('catalogue.search')} htmlFor="cat-search">
          <input id="cat-search" type="search" className="input" autoComplete="off" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('catalogue.searchPlaceholder')} data-testid="product-search" />
        </Field>
        <Field label={t('catalogue.kindFilter')} htmlFor="cat-kind">
          <select id="cat-kind" className="input" value={kind} onChange={(e) => setKind(e.target.value as KindFilter)} data-testid="product-kind-filter">
            <option value="all">{t('catalogue.kindAll')}</option>
            {PRODUCT_KINDS.map((k) => <option key={k} value={k}>{t(`catalogue.kind.${k}`)}</option>)}
          </select>
        </Field>
        <label className="flex min-h-12 items-center gap-3 font-medium">
          <input type="checkbox" className="size-6 shrink-0" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="product-show-archived" />
          {t('catalogue.showArchived')}
        </label>
      </div>
      <p className="meta" data-testid="product-count">{t('catalogue.products.count', { count: rows.length })}</p>
      {rows.length === 0 && <p className="opacity-70" data-testid="product-none">{data.products.length === 0 ? t('catalogue.products.empty') : t('catalogue.products.none')}</p>}
      <ul className="flex flex-col gap-3" data-testid="product-list">
        {rows.map((p) => (
          <ProductRowCard key={p.id} product={p} open={open === p.id} focus={focusId === p.id} lang={lang} supplier={p.supplier_id ? supplierName.get(p.supplier_id) : undefined}
            version={newest.get(p.id)} onToggle={() => { w.reset(); setOpen(open === p.id ? null : p.id) }}>
            <ProductDetail product={p} versions={versionsOf.get(p.id) ?? NONE} batches={batchesOf.get(p.id) ?? NONE} suppliers={data.suppliers}
              supplierName={supplierName} today={today} tz={site.timezone} />
          </ProductRowCard>
        ))}
      </ul>
    </section>
  )
}

function ProductRowCard({ product: p, open, focus, lang, supplier, version, onToggle, children }: {
  product: ProductRow; open: boolean; focus: boolean; lang: 'fr' | 'en'; supplier: string | undefined; version: ProductVersionRow | undefined; onToggle: () => void; children: ReactNode
}) {
  const { t } = useTranslation()
  const ref = useRef<HTMLLIElement>(null)
  useEffect(() => { if (focus) ref.current?.scrollIntoView?.({ block: 'start' }) }, [focus])
  const d = densityOf(version)
  return (
    <li ref={ref} className="card flex flex-col gap-3" data-testid="product-row" data-product={p.id}>
      <button type="button" className="flex w-full flex-col gap-2 text-left" aria-expanded={open} aria-controls={`prod-${p.id}`} onClick={onToggle} data-testid="product-toggle">
        <span className="flex items-start justify-between gap-2">
          <span className="min-w-0 break-words text-lg font-bold" data-testid="product-name-text">{p.name}</span>
          <Chevron open={open} />
        </span>
        <span className="flex flex-wrap items-center gap-1.5">
          <span data-testid="product-kind-pill"><Pill tone="gray">{t(`catalogue.kind.${p.kind}`)}</Pill></span>
          <span data-testid="product-unit-pill"><Pill tone="gray">{t('catalogue.row.unit', { unit: unitSymbol(p.base_unit_code, lang) })}</Pill></span>
          <span data-testid="product-density-pill">{d !== null ? <Pill tone="green">{densityLabel(d, lang)}</Pill> : <Pill tone="gray">{t('catalogue.density.none')}</Pill>}</span>
          {p.archived_at && <span data-testid="product-archived-pill"><Pill tone="amber">{t('catalogue.archived')}</Pill></span>}
        </span>
        {(supplier || p.reference || (p.kind === 'ppp' && p.amm_number)) && (
          <span className="meta flex flex-col gap-0.5">
            {supplier && <span data-testid="product-supplier-text">{t('catalogue.row.supplier', { name: supplier })}</span>}
            {p.reference && <span data-testid="product-reference-text">{t('catalogue.row.reference', { ref: p.reference })}</span>}
            {p.kind === 'ppp' && p.amm_number && <span data-testid="product-amm-text">{t('catalogue.row.amm', { amm: p.amm_number })}</span>}
          </span>
        )}
      </button>
      {open && children}
    </li>
  )
}
